'use client'

import {
	useSignMessage,
	useSignTransaction,
	useWallets as useSolanaWallets,
} from '@privy-io/react-auth/solana'
import { Connection } from '@solana/web3.js'
import { useMemo } from 'react'
import { deserializeSignedTransaction, serializeForSigning } from './solana-serialization'
import type { WalletCoreSolanaSigner } from './types'

export { deserializeSignedTransaction, serializeForSigning } from './solana-serialization'

export const SOLANA_RPC_URL = `https://solana-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY}`

/**
 * Privy-backed implementation of the wallet-core Solana signer port plus the
 * shared mainnet connection. Signing is silent (no Privy modal) to match the
 * previous Account Kit embedded-signer behavior.
 */
export function useSolanaWalletCore(): {
	signer: WalletCoreSolanaSigner | null
	connection: Connection
	address: string | undefined
	isReady: boolean
} {
	const { ready, wallets } = useSolanaWallets()
	const { signTransaction } = useSignTransaction()
	const { signMessage } = useSignMessage()
	// Privy returns every connected Solana wallet; this app only provisions
	// the embedded one, so the first entry is it. Revisit if external Solana
	// wallets are ever connectable (silent signing would not apply to them).
	const wallet = wallets[0]

	const connection = useMemo(() => new Connection(SOLANA_RPC_URL, { commitment: 'confirmed' }), [])

	const signer = useMemo<WalletCoreSolanaSigner | null>(() => {
		if (!wallet) return null
		return {
			address: wallet.address,
			async addSignature(tx) {
				const { signedTransaction } = await signTransaction({
					transaction: serializeForSigning(tx),
					wallet,
					options: { uiOptions: { showWalletUIs: false } },
				})
				return deserializeSignedTransaction(tx, signedTransaction)
			},
			async signMessage(message) {
				const { signature } = await signMessage({
					message,
					wallet,
					options: { uiOptions: { showWalletUIs: false } },
				})
				return signature
			},
		}
	}, [wallet, signTransaction, signMessage])

	return { signer, connection, address: wallet?.address, isReady: ready && !!wallet }
}
